import { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { cn } from '@/lib/utils';
import './rich-text-editor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Altura mínima da área de edição em px (padrão global: 200) */
  minHeight?: number;
  /** Altura máxima da área de edição em px; acima disso aparece scroll interno */
  maxHeight?: number;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Digite o conteúdo...',
  className,
  disabled = false,
  minHeight,
  maxHeight,
}: RichTextEditorProps) {
  const quillRef = useRef<ReactQuill>(null);

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        [{ color: [] }, { background: [] }],
        ['clean'],
      ],
    }),
    [],
  );

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'align',
    'link',
    'image',
    'color',
    'background',
  ];

  return (
    <div
      className={cn(
        'rich-text-editor-wrapper',
        (minHeight != null || maxHeight != null) && 'rich-text-editor-wrapper--auto-height',
        className,
      )}
      style={
        {
          ...(minHeight != null && { '--rte-min-height': `${minHeight}px` }),
          ...(maxHeight != null && { '--rte-max-height': `${maxHeight}px` }),
        } as React.CSSProperties
      }
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={(html) => {
          // Quill 2 pode emitir <p></p> para linha em branco; preserva <br> para
          // o HTML renderizar igual fora do editor.
          onChange(html.replace(/<p>(?:\s|&nbsp;)*<\/p>/gi, '<p><br></p>'));
        }}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
      />
    </div>
  );
}
